import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  ActivityIndicator,
  Linking,
  ScrollView,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@constants/colors';
import { NewsArticle } from '@/types/perplexity.types';

interface NewsSnippetsProps {
  news: NewsArticle[];
  isLoading: boolean;
  error: string | null;
  onRefresh?: () => void;
}

// Helper function to get color for impact
const getImpactColor = (impact: NewsArticle['impact']) => {
  switch (impact) {
    case 'positive':
      return Colors.market.profit;
    case 'negative':
      return Colors.market.loss;
    default:
      return Colors.accent.secondary;
  }
};

// Helper function to get icon for impact
const getImpactIcon = (impact: NewsArticle['impact']): keyof typeof Ionicons.glyphMap => {
  switch (impact) {
    case 'positive':
      return 'trending-up';
    case 'negative':
      return 'trending-down';
    default:
      return 'remove';
  }
};

export const NewsSnippets: React.FC<NewsSnippetsProps> = ({
  news,
  isLoading,
  error,
  onRefresh,
}) => {
  const handleArticlePress = (url: string) => {
    if (url) {
      Linking.openURL(url).catch(err => console.error('Error opening URL:', err));
    }
  };

  if (isLoading) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Ionicons name="newspaper-outline" size={18} color={Colors.text.secondary} />
          <Text style={styles.headerTitle}>Market News</Text>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={Colors.ai.primary} />
          <Text style={styles.loadingText}>Loading news...</Text>
        </View>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <Ionicons name="newspaper-outline" size={18} color={Colors.text.secondary} />
          <Text style={styles.headerTitle}>Market News</Text>
        </View>
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={20} color={Colors.status.error} />
          <Text style={styles.errorText}>{error}</Text>
          {onRefresh && (
            <Pressable onPress={onRefresh} style={styles.retryButton}>
              <Text style={styles.retryText}>Try Again</Text>
            </Pressable>
          )}
        </View>
      </View>
    );
  }

  if (!news || news.length === 0) {
    return null;
  }

  return (
    <View style={styles.wrapper}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Ionicons name="newspaper-outline" size={18} color={Colors.text.secondary} />
          <Text style={styles.headerTitle}>Market News</Text>
          <View style={styles.newsCount}>
            <Text style={styles.newsCountText}>{news.length}</Text>
          </View>
        </View>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.scrollContent}
        style={styles.scrollView}
      >
        {news.map((article, index) => {
          const impactColor = getImpactColor(article.impact);
          const impactIcon = getImpactIcon(article.impact);

          return (
            <Pressable
              key={index}
              style={styles.newsCard}
              onPress={() => handleArticlePress(article.url)}
            >
              {/* Impact indicator bar */}
              <View style={[styles.impactBar, { backgroundColor: impactColor }]} />

              {/* Card header with impact icon */}
              <View style={styles.cardHeader}>
                <View style={[styles.impactBadge, { backgroundColor: `${impactColor}20` }]}>
                  <Ionicons name={impactIcon} size={16} color={impactColor} />
                </View>
                <Text style={styles.source} numberOfLines={1}>
                  {article.source}
                </Text>
              </View>

              {/* Headline */}
              <Text style={styles.headline} numberOfLines={3}>
                {article.headline}
              </Text>

              {/* Summary */}
              <Text style={styles.summary} numberOfLines={3}>
                {article.summary}
              </Text>

              {/* Footer with timestamp */}
              {article.timestamp && (
                <View style={styles.cardFooter}>
                  <Ionicons name="time-outline" size={12} color={Colors.text.tertiary} />
                  <Text style={styles.timestamp}>
                    {new Date(article.timestamp).toLocaleString([], {
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Text>
                </View>
              )}
            </Pressable>
          );
        })}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  wrapper: {
    marginVertical: 12,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    marginBottom: 12,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  newsCount: {
    backgroundColor: Colors.accent.primary,
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
  },
  newsCountText: {
    fontSize: 11,
    fontWeight: '700',
    color: Colors.text.primary,
  },
  refreshButton: {
    padding: 4,
  },
  scrollView: {
    flexGrow: 0,
  },
  scrollContent: {
    paddingHorizontal: 24,
    gap: 12,
  },
  newsCard: {
    width: 300,
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    overflow: 'hidden',
  },
  impactBar: {
    height: 4,
    width: '100%',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    paddingBottom: 8,
  },
  impactBadge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  source: {
    flex: 1,
    fontSize: 12,
    fontWeight: '600',
    color: Colors.text.tertiary,
    textTransform: 'uppercase',
  },
  headline: {
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 21,
    color: Colors.text.primary,
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  summary: {
    fontSize: 13,
    lineHeight: 19,
    color: Colors.text.secondary,
    paddingHorizontal: 12,
    marginBottom: 12,
  },
  cardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 12,
    paddingBottom: 12,
  },
  timestamp: {
    fontSize: 11,
    color: Colors.text.tertiary,
  },
  container: {
    backgroundColor: Colors.background.secondary,
    marginHorizontal: 24,
    marginVertical: 12,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 20,
    gap: 12,
  },
  loadingText: {
    fontSize: 13,
    color: Colors.text.secondary,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    gap: 8,
  },
  errorText: {
    flex: 1,
    fontSize: 13,
    color: Colors.status.error,
  },
  retryButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: Colors.accent.primary,
    borderRadius: 6,
  },
  retryText: {
    fontSize: 12,
    fontWeight: '600',
    color: Colors.text.primary,
  },
});
